// Live re-pricing for the delivered cards: what this panel subscribes to, and what it recomputes.
//
// Cards arrive priced — every figure in the delivery was taken in one acquisition at the moment the
// run completed (guarantee G3). From then on they keep moving from live quotes like every other
// result surface (R5).
//
// THE ORDER NEVER MOVES (A3 / guarantee G8). This service publishes prices and nothing else. It has
// no reference to the card list's order, no sort, and no way to reach one — the column re-orders
// only when the trader asks it to. That is a structural guarantee here, not a discipline.
//
// ---------------------------------------------------------------------------------------------
// THE TICK SOURCE, and a trap worth naming
//
// The strategy-price tick is the message-bus topic 'StrategyPriceDto', published by the event-stream
// layer and consumed by LastQuoteCacheService itself.
//
// It is NOT 'Apg.StrategyPriceUpdated'. That topic looks like the obvious one — it is what the APG's
// own cells listen to — but it is REPUBLISHED BY THE APG PANEL COMPONENT
// (adjustment-pricing-grid.component.ts:2152), not by the quote cache. A panel that listened to it
// would re-price only while the APG panel happened to be open, and would look correct in every test
// where it was. Maven listens to the source.
// ---------------------------------------------------------------------------------------------

import {Injectable, OnDestroy} from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {LastQuoteCacheService} from '../../last-quote-cache.service';
import {MessageBusService} from '../../message-bus.service';
import {StrategyPriceDto} from '../../shell-communication/shell-dto-protocol';
import {QuoteDto} from '../../shell-communication/dtos/quote-dto.class';
import {HedgeOrder} from '../../hedging-grid/data-model/hedge-order';
import {HedgeOrderGroup} from '../../hedging-grid/data-model/hedge-order-group';
import {getOrderGroupTotal} from '../../hedging-grid/hg-totals-calculators';
import {MavenCard} from './maven-delivery.contract';
import {mavenCardKey} from './maven-applied-marks.service';
import {mavenHedgesLine} from './maven-card-format';
import {QuoteIndex} from '../../adjustment-pricing-grid/model/QuoteIndex';
import {StrategyCodeIndex} from '../../adjustment-pricing-grid/model/StrategyCodeIndex';
import {CashFlowAdjustment} from '../../adjustment-pricing-grid/model/CashFlowAdjustment';
import {MavenHedgeQuoteIndex} from './maven-hedge-quote-index';
// CR-20260916-003: the ORION Close tab's own close-leg pricing, for the live close-out.
import {computeComboNet, ledgerPrice} from '../../hedging-grid/copilot/copilot-pricing';
import {Leg} from '../../hedging-grid/copilot/copilot-domain';

/**
 * The redraw signal the ADJUSTMENT pop-up listens to. It reads prices off the objects it was handed
 * and redraws on this topic — the APG publishes it on every quote tick, and so does Package
 * Comparison, which opens the same dialog.
 */
export const APG_QUOTES_UPDATED_TOPIC = 'Apg.QuotesUpdated';

/** The redraw signal the HEDGE pop-up listens to. The Hedging Grid publishes it on every quote tick. */
export const HG_REFRESH_QUOTES_TOPIC = 'Hg.RefreshQuotes';

/** The message-bus topic carrying strategy prices. The SOURCE, not a panel's republish of it. */
export const STRATEGY_PRICE_TOPIC = 'StrategyPriceDto';

/** The message-bus topic carrying leg quotes. Also the source — every panel reads this one. */
export const QUOTE_TOPIC = 'QuoteDto';

/**
 * Point-value ratio for a leg's asset against the portfolio's, so an ES leg prices beside an SPX
 * one. Supplied by the caller rather than injected: it needs the PORTFOLIO's asset, which is run
 * context this service does not own. Absent, everything prices at 1 — correct whenever the run has
 * no cross-asset leg.
 */
export type MavenNormFactorResolver = (asset: string) => number;

export interface MavenPricingContext {
    defaultQty: number;
    getNormFactor?: MavenNormFactorResolver;
}

/**
 * One card's live figures, keyed the same way the applied marks are. Every one of them is in LEDGER
 * scale — per unit, the net over the default quantity. Nothing here is raw P&L (ruling L80).
 */
export interface MavenLivePrice {
    cardKey: string;
    /** Null when ANY of the card's strategy codes has no price yet — never a partial sum. */
    adjustment: number | null;
    /** The OPENING hedges alone — the "To Close" orders are excluded. Null when any opening group is unpriced. */
    hedges: number | null;
    /**
     * The breakdown's Hedges line: the opening hedges with the close-out netted in (ruling L81).
     * This is the figure the card shows; `hedges` above is the component it is built from.
     */
    hedgesLine: number | null;
    /**
     * CR-20260916-003 (ruling L63 lifted, owner 2026-09-16): the close-out priced LIVE from the
     * delivered close legs, in ledger scale. Null when any close leg is unquoted, or under
     * Ignore (no close) — the card then shows the delivered figure, as it always did.
     */
    closeOut: number | null;
    /** adjustment + hedges + close-out, in ledger scale. Null when any part of it is. */
    total: number | null;
}

@Injectable()
export class MavenCardPricingService implements OnDestroy {

    private _cards: MavenCard[] = [];
    private _context: MavenPricingContext | null = null;

    private _subscribedCodes: string[] = [];
    private _subscribedTickers: string[] = [];

    private _strategySubscription: Subscription | null = null;
    private _quoteSubscription: Subscription | null = null;

    private readonly _prices = new Subject<MavenLivePrice[]>();

    // ---- THE TWO POP-UPS' PRICE FEED ------------------------------------------------------------
    //
    // ⚠ THE POP-UPS DO NOT PRICE THEMSELVES, and this service used to price nothing they read.
    //
    // The card column is priced from the quote cache into COPIES (`groupWithLivePrices`) and the
    // sums above. The two order dialogs are a different consumer: each reads `price` off the very
    // objects it is handed — the adjustment dialog off every leg of a `CashFlowAdjustment` and the
    // adjustment's own `price`, the hedge dialog off every position and order of a
    // `HedgeStateTransaction` — and redraws when its host publishes a refresh topic. Their hosts
    // write those prices: the APG through its QuoteIndex + StrategyCodeIndex, the Hedging Grid in
    // its own quote loop. Maven handed the delivered objects over untouched and published nothing,
    // so both dialogs opened with every figure at $0.00 (owner's smoke, 2026-09-11).
    //
    // So this service now ALSO feeds the objects the dialogs read, with the dialogs' owners' own
    // mechanisms — the APG's two indexes for the adjustment, the Hedging Grid's signing rule for the
    // hedges (see maven-hedge-quote-index.ts for why the APG's hedge branch cannot serve here) — and
    // publishes the two redraw topics. It is the same subscriptions, the same ticks, one more write.
    //
    // The card column keeps reading from the cache, not from these writes: the two consumers stay
    // independent, and a wrong write here shows in a dialog, never on a card.
    private readonly _adjustmentQuoteIndex: QuoteIndex;
    private readonly _adjustmentStrategyIndex: StrategyCodeIndex;
    private readonly _hedgeQuoteIndex = new MavenHedgeQuoteIndex();

    /** Every leg ticker the two dialogs read, beyond the card's own list: the touched-test set. */
    private _dialogTickers: string[] = [];

    constructor(
        private readonly _quoteCache: LastQuoteCacheService,
        private readonly _messageBus: MessageBusService
    ) {
        this._adjustmentQuoteIndex = new QuoteIndex(_quoteCache);
        this._adjustmentStrategyIndex = new StrategyCodeIndex(_quoteCache);
    }

    /** Emits the recomputed figures whenever a strategy price or a leg quote this run watches ticks. */
    get prices() {
        return this._prices.asObservable();
    }

    /**
     * Take over a run's delivered cards: subscribe to what they need, drop what the previous run
     * needed and this one does not.
     *
     * Called once per delivery. A second Apply replaces the run, so this replaces the subscriptions
     * with it — the diff API reference-counts, so codes shared with another panel survive.
     */
    attach(cards: MavenCard[], context: MavenPricingContext): void {

        this._cards = cards ? cards.slice() : [];
        this._context = context;

        const codes = collectStrategyCodes(this._cards);

        // SUBSCRIBE from the flat list, COMBINE from the structure (ruling L61). The two are
        // deliberately different jobs: `tickers` is the subscription list, `hedges.transaction` is
        // the shape. They must describe the same set — a ticker reachable in one and not the other
        // is a defect in the delivery. It does not need a check here: an order whose ticker was
        // never subscribed has no quote, so its group prices as UNPRICED rather than as a wrong
        // number, which is the failure we want.
        const tickers = collectTickers(this._cards);

        this._quoteCache.subscribeStrategyCodesDiff(this._subscribedCodes, codes);
        this._subscribedCodes = codes;

        this._quoteCache.subscribeTickersDiff(this._subscribedTickers, tickers);
        this._subscribedTickers = tickers;

        this.indexDialogObjects();

        if (this._strategySubscription === null) {
            this._strategySubscription = this._messageBus
                .of<StrategyPriceDto[]>(STRATEGY_PRICE_TOPIC)
                .subscribe(msg => this.onStrategyPrices(msg.payload));
        }

        if (this._quoteSubscription === null) {
            this._quoteSubscription = this._messageBus
                .of<QuoteDto[]>(QUOTE_TOPIC)
                .subscribe(msg => this.onQuotes(msg.payload));
        }

        // Prime from whatever the cache already holds, so a card that was already being watched by
        // another panel does not wait for the next tick to show a live figure.
        this.publishPrices();
    }

    /**
     * Drop every subscription this service holds.
     *
     * Called when the panel is destroyed and when a run is replaced with nothing. Leaving a
     * subscription behind leaks a quote stream for a run that no longer exists.
     */
    detach(): void {

        if (this._subscribedCodes.length > 0) {
            this._quoteCache.subscribeStrategyCodesDiff(this._subscribedCodes, []);
            this._subscribedCodes = [];
        }

        if (this._subscribedTickers.length > 0) {
            this._quoteCache.unsubscribeTickers(this._subscribedTickers);
            this._subscribedTickers = [];
        }

        this.resetDialogIndexes();

        if (this._strategySubscription !== null) {
            this._strategySubscription.unsubscribe();
            this._strategySubscription = null;
        }

        if (this._quoteSubscription !== null) {
            this._quoteSubscription.unsubscribe();
            this._quoteSubscription = null;
        }

        this._cards = [];
        this._context = null;
    }

    ngOnDestroy(): void {
        this.detach();
    }

    /**
     * The live adjustment figure for one card: the sum of EVERY one of its strategy codes.
     *
     * An array, not one code (ruling L13) — a couple's cell holds two strategies. If any code has no
     * price, the cell is unpriced and this returns null: a partial sum would drift the card away
     * from the price it was delivered at and read as a pricing bug rather than a missing
     * subscription. An unpriced part is never treated as zero (guarantee G4).
     */
    liveAdjustmentPrice(card: MavenCard): number | null {

        const codes = card?.adjustment?.strategyCodes;

        if (!codes || codes.length === 0) {
            return null;
        }

        let sum = 0;

        for (const code of codes) {
            const quote = this._quoteCache.getLastStrategyPrice(code);

            if (!quote || quote.price === null || quote.price === undefined) {
                return null;
            }

            // THE SIGN, REVERSED ONCE — the same reversal the Price Box applies to the same
            // strategy price (StrategyCodeIndex: `strategyPrice.price * -1`) and the APG ORION's
            // backend pricer applies to the delivered figure. The option strategy service reports
            // "we pay" as positive; a card reads a credit as positive. Summed raw, a +$97 roll
            // credit ticked on screen as -$97 while the hedges beside it were signed right
            // (G3 defect 17). The delivered `adjustment.price` already carries this reversal.
            sum += quote.price * -1;
        }

        return sum;
    }

    /**
     * The live hedge figure for one card: every order group of every hedge transaction, combined
     * the way the hedge pop-up combines them.
     *
     * PRICED FROM `hedges.transaction`, NOT from `tickers[]` (ruling L61). The flat ticker list is a
     * subscription list and nothing more — it carries no quantity, no side and no asset, so it
     * cannot be combined into a price. The transaction carries all three, per order.
     *
     * The combination is `getOrderGroupTotal`, the hedge pop-up's OWN calculator, called rather than
     * copied: price x |qty| x norm factor per order, all-or-nothing, divided by the group's highest
     * common factor and rescaled to the default quantity. Calling it is the only way the card and
     * the pop-up cannot disagree — a re-implementation drifts the first time either is touched.
     *
     * The sign lives on the PRICE, not the quantity: an order's live price is
     * `mid * sign(qty) * -1`, so a bought leg contributes a debit and a sold leg a credit, while the
     * calculator takes |qty|.
     *
     * ⚠ THREE SIGN CONVENTIONS LIVE IN THIS CODEBASE, one per object, and none of the three files
     * says so (ruling L62):
     *
     *   order leg     `Math.sign(order.qty) * -1`      hedging-grid.component.ts:873, :898
     *   position leg  `Math.sign(bpos.qty)`            hedging-grid.component.ts:846
     *   close leg     `leg.side === 'sell' ? 1 : -1`   copilot-pricing.ts:40
     *
     * All three then take `Math.abs(qty)`. Carry one across to another object and the price INVERTS:
     * a credit renders as a debit, nothing throws, and the number stays entirely plausible. This
     * service prices ORDER legs, so it uses the first.
     *
     * ⚠ THE CLOSE DID REACH HERE, as "To Close" order groups inside the same transaction, and was
     * priced as if it were an opening hedge (defect found by the owner's smoke, 2026-09-13). The
     * loop below now prices the OPENING orders only; the close-out stays the delivered figure that
     * liveTotal adds once.
     *
     * Null if ANY opening group is unpriced: a partial hedge is not a smaller hedge (guarantee G4).
     */
    liveHedgePrice(card: MavenCard): number | null {

        const transactions = card?.hedges?.transaction;

        if (!transactions || transactions.length === 0 || this._context === null) {
            return null;
        }

        const defaultQty = this._context.defaultQty;
        const getNormFactor = this._context.getNormFactor || (() => 1);

        let sum = 0;
        let sawGroup = false;

        for (const transaction of transactions) {

            const groups = transaction?.orderTransactions;

            if (!groups) {
                continue;
            }

            for (const group of groups) {

                // OPENING ORDERS ONLY. Under Close the delivered transaction carries the saved
                // book's closing orders as their own groups ("Sell To Close" / "Buy To Close",
                // CashFlowHedgesCalculatorService) beside the "To Open" groups of the new hedges.
                // Priced together they made this figure the NET of open and close, and liveTotal
                // then added the delivered close-out on top — the close counted twice, and the
                // owner saw a bought vertical rendered as a credit (smoke, 2026-09-13). The close
                // is the Hedge Matrix ORION's figure, taken as delivered (ruling L63); it is never
                // priced here. A group with no opening order is a pure close and is skipped.
                const opening = openingOrdersOf(group);

                if (opening === null) {
                    continue;
                }

                const priced = this.groupWithLivePrices(opening);

                if (priced === null) {
                    return null;
                }

                const total = getOrderGroupTotal(priced, defaultQty, getNormFactor);

                if (total === null || total === undefined || isNaN(total)) {
                    return null;
                }

                sum += total;
                sawGroup = true;
            }
        }

        return sawGroup ? sum : null;
    }

    /**
     * The live total: adjustment + hedges + close-out (ruling L61).
     *
     * The delivered `Total` is composed of the same three parts, and the live one has to be composed
     * the same way — assembled differently, the card drifts away from the figure it was ranked on
     * the moment quotes start arriving, and reads as a ranking defect rather than an arithmetic one.
     *
     * THE CLOSE-OUT TERM IS LIVE TOO (CR-20260916-003; this LIFTS ruling L63, owner 2026-09-16).
     * Since that CR the Setup shows the same close priced live, and two figures for one close that
     * drift apart as the market moves is the worse outcome. L63's objection was reproducing the
     * backend's LedgerPrice on the third sign convention (close legs are side-driven): that is
     * exactly what the ORION Close tab's own `copilot-pricing` encodes — sell = credit, buy = debit,
     * mid × |qty| × norm factor, net / HCF, scaled to the default quantity — so the close legs
     * price through it, not through a Maven formula. The delivered figure remains the fallback
     * while any close leg is unquoted.
     *
     * Null if any part is null — never a partial total.
     */
    liveTotal(card: MavenCard): number | null {

        const adjustment = this.liveAdjustmentPrice(card);

        if (adjustment === null) {
            return null;
        }

        const hedges = this.liveHedgePrice(card);

        if (hedges === null) {
            return null;
        }

        return adjustment + hedges + this.closeOutTerm(card);
    }

    /**
     * The close-out of a card, priced LIVE from its delivered close legs (CR-20260916-003), in
     * ledger scale. Null under Ignore (no close on the card) and while any close leg has no quote.
     */
    liveCloseOut(card: MavenCard): number | null {

        const legs = card?.hedges?.close?.legs;

        if (!legs || legs.length === 0 || this._context === null) {
            return null;
        }

        const getNormFactor = this._context.getNormFactor || (() => 1);

        const copilotLegs: Leg[] = [];

        for (const leg of legs) {
            if (!leg || !leg.ticker || (leg.side !== 'sell' && leg.side !== 'buy')) {
                return null;
            }
            copilotLegs.push({
                quantity: leg.quantity,
                ticker: leg.ticker,
                tickerDisplayName: leg.displayName || leg.ticker,
                action: leg.action || '',
                side: leg.side,
                effect: 'close',
                asset: leg.asset
            });
        }

        const net = computeComboNet(
            copilotLegs,
            ticker => {
                const quote = this._quoteCache.getLastQuote(ticker);
                return (quote && quote.mid !== null && quote.mid !== undefined && !isNaN(quote.mid))
                    ? quote.mid
                    : undefined;
            },
            asset => getNormFactor(asset || '')
        );

        const ledger = ledgerPrice(net, this._context.defaultQty);

        return (ledger === undefined || ledger === null || isNaN(ledger)) ? null : ledger;
    }

    // The close-out as a TERM of the total: live when priced, the delivered figure otherwise, and
    // zero under Ignore (nothing was ordered and nothing belongs in the package — a zero term, not
    // a missing one).
    private closeOutTerm(card: MavenCard): number {
        const live = this.liveCloseOut(card);
        if (live !== null) {
            return live;
        }
        const delivered = card?.prices?.closeOut;
        return (delivered === null || delivered === undefined) ? 0 : delivered;
    }

    /**
     * A copy of an order group whose orders carry live prices, or null when any order has no quote.
     *
     * A COPY, for two reasons: the delivered transaction is the pop-up's input and must reach it
     * exactly as delivered, and `getOrderGroupTotal` writes `hcf` onto the group it is given.
     */
    private groupWithLivePrices(group: HedgeOrderGroup): HedgeOrderGroup | null {

        const orders = group?.orders;

        if (!orders || orders.length === 0) {
            return null;
        }

        const livePriced: HedgeOrder[] = [];

        for (const order of orders) {

            if (!order || !order.ticker) {
                return null;
            }

            const quote = this._quoteCache.getLastQuote(order.ticker);

            if (!quote || quote.mid === null || quote.mid === undefined || isNaN(quote.mid)) {
                return null;
            }

            livePriced.push({
                ...order,
                price: quote.mid * Math.sign(order.qty) * -1,
                hcf: undefined,
            });
        }

        return {...group, orders: livePriced, hcf: undefined};
    }

    private onStrategyPrices(prices: StrategyPriceDto[]): void {

        if (!prices || prices.length === 0) {
            return;
        }

        // The adjustment dialog's own price line: written onto the CashFlowAdjustment objects the
        // way the APG writes it, then the dialog is told to redraw. Driven by what the index
        // actually updated, not by the card's code list — the index knows which objects it holds.
        const dialogUpdates = this._adjustmentStrategyIndex.onStrategyPrice(prices);

        if (dialogUpdates && dialogUpdates.length > 0) {
            this._messageBus.publishAsync({topic: APG_QUOTES_UPDATED_TOPIC, payload: {}});
        }

        // Only wake the column when a code this run actually watches has moved.
        const touched = prices.some(p => p && this._subscribedCodes.indexOf(p.strategyCode) >= 0);

        if (!touched) {
            return;
        }

        this.publishPrices();
    }

    private onQuotes(quotes: QuoteDto[]): void {

        if (!quotes || quotes.length === 0) {
            return;
        }

        const touched = quotes.some(q => q
            && (this._subscribedTickers.indexOf(q.ticker) >= 0 || this._dialogTickers.indexOf(q.ticker) >= 0));

        if (!touched) {
            return;
        }

        // Write the legs the two dialogs read, then wake both. Each dialog ignores the signal while
        // hidden (the adjustment one holds a pending flag and redraws on a 1 s timer; the hedge one
        // filters on `visible`), so publishing on every touched tick costs nothing when neither is
        // open — and it is exactly what their own hosts do.
        this._adjustmentQuoteIndex.onQuote(quotes);
        this._hedgeQuoteIndex.onQuote(quotes);
        this._messageBus.publishAsync({topic: APG_QUOTES_UPDATED_TOPIC, payload: {}});
        this._messageBus.publishAsync({topic: HG_REFRESH_QUOTES_TOPIC, payload: {}});

        this.publishPrices();
    }

    /**
     * Index every object the two dialogs read, across the whole run, and prime them from the cache.
     *
     * The whole run rather than the selected card: the dialogs open for whichever card the trader
     * picks, and the cheapest correct moment to index is once, at delivery, on the objects the cards
     * already hold. `attach` replaces a run, so the previous run's indexes are dropped first.
     */
    private indexDialogObjects(): void {

        this.resetDialogIndexes();

        const adjustments: CashFlowAdjustment[] = [];

        this._cards.forEach(card => {

            (card?.adjustment?.adjustments || []).forEach(adjustment => {
                if (adjustment) {
                    adjustments.push(adjustment);
                }
            });

            this._hedgeQuoteIndex.add(card?.hedges?.transaction);
        });

        if (adjustments.length > 0) {
            // Both subscribe what they index, reference-counted beside this service's own
            // subscriptions, and unsubscribe it again on reset().
            this._adjustmentQuoteIndex.addData(adjustments);
            this._adjustmentStrategyIndex.addData(adjustments);
        }

        // The hedge index subscribes nothing itself; its tickers are subscribed here, beside the
        // card's own list. Reference-counted, so an overlap with `tickers[]` is harmless.
        const hedgeTickers = this._hedgeQuoteIndex.tickers;

        if (hedgeTickers.length > 0) {
            this._quoteCache.subscribeTickers(hedgeTickers);
        }

        this._dialogTickers = hedgeTickers.concat(collectAdjustmentLegTickers(adjustments));

        // Prime from what the cache already holds, so a dialog opened before the next tick is not
        // blank. Both index `onQuote` calls skip anything they do not index, so the whole cache is
        // an acceptable input once per delivery.
        const cachedQuotes = this._quoteCache.getAllQuotes();

        if (cachedQuotes.length > 0) {
            this._adjustmentQuoteIndex.onQuote(cachedQuotes);
            this._hedgeQuoteIndex.onQuote(cachedQuotes);
        }

        const cachedStrategyPrices = this._quoteCache.getAllLastStrategyPrices();

        if (cachedStrategyPrices.length > 0) {
            this._adjustmentStrategyIndex.onStrategyPrice(cachedStrategyPrices);
        }
    }

    private resetDialogIndexes(): void {

        // Each unsubscribes what it subscribed. Order-independent: every subscription is
        // reference-counted in the cache.
        this._adjustmentQuoteIndex.reset();
        this._adjustmentStrategyIndex.reset();

        const hedgeTickers = this._hedgeQuoteIndex.tickers;

        if (hedgeTickers.length > 0) {
            this._quoteCache.unsubscribeTickers(hedgeTickers);
        }

        this._hedgeQuoteIndex.reset();
        this._dialogTickers = [];
    }

    private publishPrices(): void {

        if (this._cards.length === 0) {
            return;
        }

        this._prices.next(
            this._cards.map(card => {
                const hedges = this.liveHedgePrice(card);
                const closeOut = this.liveCloseOut(card);

                return {
                    cardKey: mavenCardKey(card),
                    adjustment: this.liveAdjustmentPrice(card),
                    hedges: hedges,
                    // The netted line takes the live close-out when priced, the delivered one otherwise.
                    hedgesLine: mavenHedgesLine(hedges, closeOut !== null ? closeOut : card?.prices?.closeOut),
                    closeOut: closeOut,
                    total: this.liveTotal(card),
                };
            })
        );
    }
}

/**
 * The group reduced to its OPENING orders, or null when it has none (a pure closing group).
 *
 * The marker is the calculator's own action wording: every closing order it emits reads
 * "... To Close" (CashFlowHedgesCalculatorService:92, :179). Everything else — "To Open", and the
 * modify-path wordings that cannot arise on a Maven run because the saved book leaves the desired
 * state whole — is an opening order. Filtering orders does not disturb the group total: the HCF
 * `getOrderGroupTotal` divides by is the one it multiplies back, so the figure is the plain net
 * over the default quantity either way.
 */
function openingOrdersOf(group: HedgeOrderGroup): HedgeOrderGroup | null {

    const orders = group?.orders;

    if (!orders || orders.length === 0) {
        return null;
    }

    const opening = orders.filter(o => !isClosingOrder(o));

    if (opening.length === 0) {
        return null;
    }

    return opening.length === orders.length ? group : {...group, orders: opening};
}

function isClosingOrder(order: HedgeOrder): boolean {
    return typeof order?.action === 'string' && order.action.toLowerCase().indexOf('to close') >= 0;
}

/**
 * Every leg ticker the adjustment dialog reads, de-duplicated: the six leg arrays the APG's
 * QuoteIndex indexes. Used only for the touched-test on a quote tick; the index itself subscribes.
 */
export function collectAdjustmentLegTickers(adjustments: CashFlowAdjustment[]): string[] {

    const seen: string[] = [];

    function take(legs: { ticker?: string }[] | null | undefined): void {
        (legs || []).forEach(leg => {
            if (leg && leg.ticker && seen.indexOf(leg.ticker) < 0) {
                seen.push(leg.ticker);
            }
        });
    }

    (adjustments || []).forEach(adjustment => {
        if (!adjustment) {
            return;
        }
        take(adjustment.beforeState);
        take(adjustment.afterState);
        take(adjustment.mainLegs);
        take(adjustment.linkedLegs);
        take(adjustment.secondLinkedLegs);
        take(adjustment.thirdLinkedLegs);
    });

    return seen;
}

/** Every strategy code across the run, de-duplicated. */
export function collectStrategyCodes(cards: MavenCard[]): string[] {

    const seen: string[] = [];

    (cards || []).forEach(card => {
        (card?.adjustment?.strategyCodes || []).forEach(code => {
            if (code && seen.indexOf(code) < 0) {
                seen.push(code);
            }
        });
    });

    return seen;
}

/**
 * Every leg ticker across the run, de-duplicated.
 *
 * The delivery carries these as a flat list per card, for exactly this purpose: so the browser can
 * subscribe to the legs' quotes.
 */
export function collectTickers(cards: MavenCard[]): string[] {

    const seen: string[] = [];

    (cards || []).forEach(card => {
        (card?.tickers || []).forEach(ticker => {
            if (ticker && seen.indexOf(ticker) < 0) {
                seen.push(ticker);
            }
        });
    });

    return seen;
}
